import {Sync} from '@client/sync'
import {TaskWithColor} from '@components/calendar/calendar-store'
import {MessageBox} from '@components/message-box'
import {ChatUtils} from '@extensions/chat-utils'
import {
  differenceInMinutes,
  endOfDay,
  format,
  isSameDay,
  isValid,
  startOfDay
} from 'date-fns'
import {ru} from 'date-fns/locale'
import React from 'react'
import {Lang, Language} from 'yougile-lang'
import {Hub, TaskData} from 'yougile-model'
import {Util} from 'yougile-util'
import {DraggableStore} from './week/draggable/draggable-store'

export const minTaskHeight = 30

export const lightenColor = (color: string, percent: number) => {
  const num = parseInt(color.replace('#', ''), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    B = ((num >> 8) & 0x00ff) + amt,
    G = (num & 0x0000ff) + amt
  return (
    '#' +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
      (G < 255 ? (G < 1 ? 0 : G) : 255)
    )
      .toString(16)
      .slice(1)
  )
}

export const roundNumberTo = (val: number, coefficient: number) => {
  return Math.round(val / coefficient) * coefficient
}

export const roundedTo15Min = (val: Date) => {
  const coefficient = 1000 * 60 * 15
  return new Date(Math.round(val.getTime() / coefficient) * coefficient)
}

export const proportionPixelsDivideOnHour = 1
export const proportionHourDivideOnPixels = 1

export const openTask = (task: TaskData) => {
  const box = Sync.getCurrentSandbox()
  const taskHub = box.get(task.id) as Hub
  if (!taskHub) {
    return
  }
  const type = box.getType(taskHub)
  if (type !== 'task') {
    return
  }
  if (taskHub.deleted) {
    return MessageBox.warn(
      `${Util.capitalize(Lang.msg.statActionObjTask)} ${
        Lang.msg.statActionDeleteOther
      }`
    )
  }

  const openedChatHub = ChatUtils.getOpenedChatHub()
  if (openedChatHub?.id === taskHub.id && ChatUtils.isChatExpanded()) {
    ChatUtils.closeChatIfOpened()
  } else {
    ChatUtils.openChat(taskHub)
  }
}

export const roundMinutes = (date: Date) => {
  date.setHours(date.getHours() + Math.round(date.getMinutes() / 60))
  date.setMinutes(0, 0, 0)

  return date
}

export const getDeadline = (
  task: TaskWithColor,
  draggableState: DraggableStore['state']
) => {
  return draggableState.draggedItem.id === task.task.id &&
    draggableState.draggedItem.changeAction === 'change-deadline' &&
    draggableState.deadline
    ? draggableState.deadline
    : task.roundedDeadline
}

export const validDateFormat = (date: Date, formatString: string) => {
  if (!isValid(date)) {
    return null
  }

  return format(date, formatString)
}

export const getTaskHeight = (
  startDate: Date,
  deadline: Date,
  opts?: {
    allDay?: boolean
    startFromZero?: boolean
  }
) => {
  const {allDay, startFromZero} = opts || {}

  const diffMinutes = startFromZero
    ? differenceInMinutes(deadline, startOfDay(deadline))
    : isSameDay(startDate, deadline)
    ? differenceInMinutes(deadline, startDate)
    : differenceInMinutes(endOfDay(startDate), startDate)

  const allDayHeight = 24 * 60
  const height = allDay
    ? allDayHeight
    : diffMinutes * proportionPixelsDivideOnHour

  return height - 1
}

export const hours = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23
]

export const getTasksWithShiftLeft = (
  tasksWithStartDate: TaskWithColor[],
  startWith = 0
): TaskWithColor[] => {
  const currentTask = tasksWithStartDate[startWith]
  const nextTask = tasksWithStartDate[startWith + 1]

  const tasks: TaskWithColor[] = new Array(tasksWithStartDate.length)

  for (let ix = 0; ix < tasksWithStartDate.length; ix++) {
    const task = tasksWithStartDate[ix]

    if (
      startWith < ix &&
      currentTask.task.id !== task.task.id &&
      currentTask.roundedDeadline > task.roundedStartDate &&
      (isSameDay(task.roundedDeadline, currentTask.roundedDeadline) ||
        isSameDay(task.roundedStartDate, currentTask.roundedStartDate))
    ) {
      tasks[ix] = {
        ...task,
        marginShiftLeft: Math.max(
          task.marginShiftLeft + 1,
          currentTask.marginShiftLeft + 1
        )
      }
    } else {
      tasks[ix] = task
    }
  }

  if (nextTask) {
    return getTasksWithShiftLeft(tasks, startWith + 1)
  }

  return tasks
}

export const getTasksWithShiftRight = (
  tasksWithStartDate: TaskWithColor[],
  startWith = tasksWithStartDate.length - 1
): TaskWithColor[] => {
  const currentTask = tasksWithStartDate[startWith]
  const nextTask = tasksWithStartDate[startWith - 1]

  const tasks: TaskWithColor[] = new Array(tasksWithStartDate.length)

  for (let ix = 0; ix < tasksWithStartDate.length; ix++) {
    const task = tasksWithStartDate[ix]

    if (
      startWith > ix &&
      currentTask.task.id !== task.task.id &&
      currentTask.roundedStartDate < task.roundedDeadline &&
      nextTask?.marginShiftRight === task.marginShiftRight &&
      (isSameDay(task.roundedDeadline, currentTask.roundedDeadline) ||
        isSameDay(task.roundedStartDate, currentTask.roundedStartDate))
    ) {
      tasks[ix] = {
        ...task,
        marginShiftRight: Math.max(
          task.marginShiftRight + 1,
          currentTask.marginShiftRight + 1
        )
      }
    } else {
      tasks[ix] = task
    }
  }

  if (nextTask) {
    return getTasksWithShiftRight(tasks, startWith - 1)
  }

  return tasks
}

export const getTasksWithLargeShift = (
  tasksWithStartDate: TaskWithColor[]
): TaskWithColor[] => {
  const lastTask = tasksWithStartDate[tasksWithStartDate.length - 1]

  const tasks: TaskWithColor[] = new Array(tasksWithStartDate.length)

  for (let ix = 0; ix < tasksWithStartDate.length; ix++) {
    const task = tasksWithStartDate[ix]
    const nextTask = tasksWithStartDate[ix + 1]
    const nextTaskStartDate = nextTask?.roundedStartDate
    if (
      (task.marginShiftLeft === 1 &&
        nextTaskStartDate >= task.roundedDeadline) ||
      (!nextTask && lastTask.marginShiftLeft === 1)
    ) {
      tasks[ix] = {
        ...task,
        marginShiftLeft: 4
      }
    } else {
      tasks[ix] = task
    }
  }

  return tasks
}

export const capitalizeDateFormat = (
  date: Date,
  language: Language,
  formatString: string
) => {
  if (!isValid(date)) {
    return null
  }

  return (
    <span className="capitalize">
      {format(date, formatString, {
        locale: language === 'ru' && ru
      }).replaceAll('. ', ' ')}
    </span>
  )
}
